import { ChatBubbleLeftIcon, PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";

import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Badge } from "@fronterahealth/frontera-ui-components/ui/badge";

import { ReportReviewerStatus, ReportSectionFieldsType } from "@api/graphql/types-and-hooks";
import { isEditSuggestableFieldType } from "@components/QaReview/PeerReview/peerReview.suggestedEdit";
import { FieldCountBadge } from "@components/QaReview/components/FieldCountBadge";
import { useCanComment } from "@components/QaReview/hooks/useCanComment";
import { useGoToItem } from "@components/QaReview/hooks/useGoToItem";
import { useIsReviewPanelVisible } from "@components/QaReview/hooks/useIsReviewPanelVisible";
import { useReviewAnchors } from "@components/QaReview/hooks/useReviewAnchors";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { RevealOnFieldHover } from "@components/ReportField/RevealOnFieldHover";
import { useCanReviewReport } from "@components/ReportReview/hooks/useCanReviewReport";
import { useSidebarView } from "@providers/SidebarViewProvider/useSidebarView";

interface PeerReviewFieldLabelBadgesProps {
  field: ReportSectionFieldsType;
}

export const PeerReviewFieldLabelBadges: React.FC<PeerReviewFieldLabelBadgesProps> = ({ field }) => {
  const { commentCountForField, firstOpenCommentForField } = useReviewAnchors();
  const { state, dispatch } = useReviewSession();
  const { setView } = useSidebarView();
  const goToItem = useGoToItem();
  const canComment = useCanComment();
  const isReviewPanelVisible = useIsReviewPanelVisible();
  // A reviewer who hasn't decided yet sees the actions without hovering.
  const isPendingReviewer = useCanReviewReport()?.status === ReportReviewerStatus.Requested;

  const commentCount = commentCountForField(field.id);
  const commentCountLabel = `${commentCount} ${commentCount === 1 ? "comment" : "comments"}`;
  const isSuggesting = state.suggestEditFieldId === field.id;
  // While "On this page" shows, an open composer is off screen, so "+ Comment" brings it back
  // (with its draft) instead of closing it.
  const isComposing = isReviewPanelVisible && state.composer?.kind === "comment" && state.composer.fieldId === field.id;

  const showComments = () => {
    setView("review");
    const firstComment = firstOpenCommentForField(field.id);
    if (firstComment) goToItem(firstComment);
  };

  return (
    <>
      {commentCount > 0 && (
        <FieldCountBadge
          count={commentCount}
          label={commentCountLabel}
          icon={<ChatBubbleLeftIcon data-icon="inline-start" />}
          className={cn(isComposing ? "bg-blue-100" : "bg-transparent hover:bg-blue-50", "text-blue-600")}
          onClick={showComments}
        />
      )}

      {canComment && (
        <RevealOnFieldHover alwaysVisible={isPendingReviewer}>
          <div className="flex items-center gap-1">
            <Badge
              variant="outline"
              size="sm"
              aria-label="Add comment"
              className="cursor-pointer border-dashed text-text-tertiary"
              onClick={() => {
                if (isComposing) {
                  dispatch({ type: "closeComposer" });
                  return;
                }
                setView("review");
                dispatch({
                  type: "openComposer",
                  composer: { kind: "comment", fieldId: field.id, fieldTitle: field.title ?? "" },
                });
              }}
            >
              <PlusIcon data-icon="inline-start" /> Comment
            </Badge>
            {isEditSuggestableFieldType(field.fieldType) && (
              <Badge
                variant="outline"
                size="sm"
                aria-label="Suggest edit"
                className="cursor-pointer border-dashed text-text-tertiary"
                onClick={() => {
                  if (isSuggesting) {
                    dispatch({ type: "setSuggestEditField", fieldId: null });
                    return;
                  }
                  setView("review");
                  dispatch({ type: "setSuggestEditField", fieldId: field.id });
                }}
              >
                <PencilSquareIcon data-icon="inline-start" /> Suggest edit
              </Badge>
            )}
          </div>
        </RevealOnFieldHover>
      )}
    </>
  );
};
